import { useEffect, useState } from 'react';
import type { Appearance } from '@stripe/stripe-js';

type StripeAppearanceState = {
  appearance: Appearance;
  key: string;
};

const fallbackState: StripeAppearanceState = {
  key: 'stripe-light',
  appearance: {
    theme: 'stripe',
    variables: {
      colorPrimary: '#e8421a',
      colorText: '#0d0d0d',
      colorTextSecondary: '#625d53',
      colorBackground: '#ffffff',
      colorDanger: '#d92d20',
      borderRadius: '9px',
    },
  },
};

export function useStripeAppearance() {
  const [state, setState] = useState<StripeAppearanceState>(() => getStripeAppearance());

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const updateAppearance = () => setState(getStripeAppearance());

    mediaQuery.addEventListener('change', updateAppearance);
    updateAppearance();

    return () => mediaQuery.removeEventListener('change', updateAppearance);
  }, []);

  return state;
}

function getStripeAppearance(): StripeAppearanceState {
  if (typeof window === 'undefined') return fallbackState;

  const rootStyles = window.getComputedStyle(document.documentElement);
  const colorScheme = rootStyles.colorScheme;
  const dark = colorScheme.split(' ').includes('dark') || window.matchMedia('(prefers-color-scheme: dark)').matches;
  const readCssVar = (name: string, fallback: string) => rootStyles.getPropertyValue(name).trim() || fallback;

  const background = readCssVar('--af-card-bg', dark ? '#151513' : '#ffffff');
  const text = readCssVar('--af-text', dark ? '#f5f5f0' : '#0d0d0d');
  const muted = readCssVar('--af-muted', dark ? '#9e9c91' : '#625d53');
  const border = readCssVar('--af-border', dark ? 'rgba(245, 245, 240, 0.08)' : 'rgba(124, 118, 106, 0.2)');
  const primary = readCssVar('--af-cta', '#e8421a');
  const danger = readCssVar('--af-danger', '#e8421a');

  return {
    key: dark ? 'stripe-dark' : 'stripe-light',
    appearance: {
      theme: dark ? 'night' : 'stripe',
      variables: {
        colorPrimary: primary,
        colorText: text,
        colorTextSecondary: muted,
        colorTextPlaceholder: muted,
        colorBackground: background,
        colorDanger: danger,
        iconColor: muted,
        borderRadius: '9px',
      },
      rules: {
        '.Input': {
          border: `1px solid ${border}`,
          boxShadow: 'none',
        },
        '.Input:focus': {
          border: `1px solid ${primary}`,
          boxShadow: `0 0 0 1px ${primary}`,
        },
        '.Tab': {
          border: `1px solid ${border}`,
          boxShadow: 'none',
        },
      },
    },
  };
}
